import { useEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Check, Loader2, Search, SkipForward, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { haptics } from '@/lib/haptics';
import { useStableObjectUrl } from '@/lib/useStableObjectUrl';
import type { BulkMatchDecision, MatchedItem, QueuePair } from './useBulkMatchQueue';


interface Props {
  pair: QueuePair;
  index: number;
  total: number;
  decision: BulkMatchDecision | undefined;
  onAttach: (item: MatchedItem) => void;
  onDraft: () => void;
  onSkip: () => void;
  onSwap?: () => void;
  disabled?: boolean;
}

/** Focused one-pair-at-a-time match card, modeled after BulkListPriceQueueDialog. */
export function BulkMatchQueueBody({
  pair,
  index,
  total,
  decision,
  onAttach,
  onDraft,
  onSkip,
  onSwap,
  disabled,
}: Props) {
  const frontUrl = useObjectUrl(pair.front);
  const backUrl = useObjectUrl(pair.back);
  const [mode, setMode] = useState<'draft' | 'attach'>('attach');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MatchedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // Reset per-pair state whenever the queue advances. Default to attach so
  // matches surface immediately without an extra tap.
  useEffect(() => {
    setMode('attach');
    setQuery('');
    setResults([]);
  }, [pair.key]);

  // Autofocus search when switching to attach.
  useEffect(() => {
    if (mode === 'attach') {
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [mode]);

  // Debounced item search with cancellation.
  useEffect(() => {
    if (mode !== 'attach') return;
    const ac = new AbortController();
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await fetchCandidateItems(query.trim(), ac.signal);
        if (!ac.signal.aborted) setResults(rows);
      } catch (err) {
        if (!ac.signal.aborted) {
          console.error('bulk match search failed', err);
          setResults([]);
        }
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, query ? 300 : 0);
    return () => {
      ac.abort();
      clearTimeout(handle);
    };
  }, [query, mode]);

  const pct = total > 0 ? Math.round((index / total) * 100) : 0;
  const alreadyMatched = decision?.kind === 'matched' ? decision.item : null;

  return (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      {/* Progress header */}
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs tabular-nums text-muted-foreground">
          {Math.min(index + 1, total)} / {total}
        </span>
        <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full bg-active-gold transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={onSkip}
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.04] text-muted-foreground hover:text-foreground disabled:opacity-40"
          title="Skip this pair (S)"
        >
          <SkipForward className="w-3 h-3" /> Skip
        </button>
      </div>

      {/* Photos */}
      <div className="flex items-start gap-2 shrink-0">
        <div className="relative flex-1 aspect-[3/4] max-h-[20dvh] rounded-xl overflow-hidden bg-black/60 border border-white/[0.06]">
          {frontUrl && (
            <img src={frontUrl} alt="front" className="absolute inset-0 w-full h-full object-contain" />
          )}
          <span className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded bg-black/70 text-white/90">
            front
          </span>
          {onSwap && pair.back && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                haptics.tap();
                onSwap();
              }}
              title="Swap front/back"
              className="absolute top-2 right-2 grid place-items-center h-8 w-8 rounded-full bg-black/70 hover:bg-active-gold/80 hover:text-black text-white/90 border border-white/10 active:scale-95 transition disabled:opacity-40"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
          )}

        </div>
        {pair.back && (
          <div className="relative w-24 aspect-[3/4] max-h-[20dvh] rounded-lg overflow-hidden bg-black/60 border border-white/[0.06] shrink-0">
            {backUrl && (
              <img src={backUrl} alt="back" className="absolute inset-0 w-full h-full object-contain" />
            )}
            <span className="absolute top-1 left-1 text-[9px] px-1 rounded bg-black/70 text-white/90">
              back
            </span>
          </div>
        )}
      </div>

      {/* Mode chips */}
      <div className="grid grid-cols-2 gap-2 shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode('draft')}
          className={cn(
            'h-10 rounded-full text-sm font-medium border transition',
            mode === 'draft'
              ? 'bg-active-gold/10 text-active-gold border-active-gold/40'
              : 'bg-white/[0.04] text-foreground/70 border-white/[0.08] hover:bg-white/[0.08]',
          )}
        >
          New draft
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode('attach')}
          className={cn(
            'h-10 rounded-full text-sm font-medium border transition',
            mode === 'attach'
              ? 'bg-active-gold/10 text-active-gold border-active-gold/40'
              : 'bg-white/[0.04] text-foreground/70 border-white/[0.08] hover:bg-white/[0.08]',
          )}
        >
          Attach to existing
        </button>
      </div>

      {mode === 'draft' ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 text-xs text-muted-foreground">
          Creates a new draft item with these photos. Press{' '}
          <kbd className="px-1 rounded bg-white/[0.06] text-foreground">Enter</kbd> to continue.
        </div>
      ) : (
        <div className="flex flex-col gap-2 min-h-0">
          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, set, card #…"
              className="w-full h-11 pl-9 pr-9 rounded-full bg-white/[0.05] border border-white/[0.08] text-sm focus:outline-none focus:border-active-gold/40 [&::-webkit-search-cancel-button]:hidden"
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="off"
              autoCorrect="off"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/[0.08]"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 rounded-lg">
            {loading && results.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2 align-[-2px]" />
                Searching…
              </div>
            ) : results.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground">
                {query ? 'No items matched.' : 'Type to search your inventory.'}
              </div>
            ) : (

              <ul className="flex flex-col gap-1">
                {results.map((item) => {
                  const thumb = item.thumb_url || item.stock_image_url;
                  const isStockOnly = !!item.stock_image_url && item.photo_count === 0;
                  const isChosen = alreadyMatched?.id === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => onAttach(item)}
                        className={cn(
                          'w-full flex items-center gap-3 p-2 rounded-lg text-left transition',
                          isChosen
                            ? 'bg-active-gold/10 border border-active-gold/40'
                            : 'hover:bg-white/[0.05] border border-transparent',
                        )}
                      >
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-black/40 shrink-0 grid place-items-center">
                          {thumb ? (
                            <img src={thumb} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[9px] text-muted-foreground">No pic</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground truncate">
                            {item.title || 'Untitled item'}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {[item.set_name, item.card_number].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </div>
                        {isChosen ? (
                          <Check className="w-4 h-4 text-active-gold shrink-0" />
                        ) : isStockOnly ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-active-gold/10 text-active-gold border border-active-gold/40 shrink-0">
                            Stock
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const useObjectUrl = useStableObjectUrl;


async function fetchCandidateItems(query: string, signal?: AbortSignal): Promise<MatchedItem[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  let q = supabase
    .from('user_items')
    .select('id, title, set_name, card_number, stock_image_url, images:user_item_images(image_url, display_order)')
    .eq('user_id', user.id)
    .is('sold_at', null)
    .order('updated_at', { ascending: false })
    .limit(20);
  if (query) {
    const safe = query.replace(/[%_]/g, ' ').slice(0, 80);
    q = q.or(`title.ilike.%${safe}%,set_name.ilike.%${safe}%,card_number.ilike.%${safe}%`);
  }
  const { data, error } = await q;
  if (error) throw error;
  if (signal?.aborted) return [];
  type Row = {
    id: string;
    title: string | null;
    set_name: string | null;
    card_number: string | null;
    stock_image_url: string | null;
    images: { image_url: string; display_order: number | null }[] | null;
  };
  return ((data ?? []) as Row[]).map((r): MatchedItem => {
    const images = [...(r.images ?? [])].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );
    return {
      id: r.id,
      title: r.title,
      set_name: r.set_name,
      card_number: r.card_number,
      stock_image_url: r.stock_image_url,
      thumb_url: images[0]?.image_url ?? null,
      photo_count: images.length,
    };
  });
}
